import type { QueryClient } from '@tanstack/react-query';
import { removeApplicationsById } from '@/lib/applicationArchive';
import {
  createApplicationListSync,
  resolveApplicationPositionId,
  type ApplicationListSync,
} from '@/lib/applicationListSync';
import { removeRecentSettledApplications } from '@/lib/recentSettledApplications';
import { queryKeys } from '@/lib/queryKeys';

export { createApplicationListSync, resolveApplicationPositionId };
export type { ApplicationListSync };
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { queryKeys } from '@/lib/queryKeys';
import type { Application, JobPosition } from '@/types';

export function setPositionsCache(
  queryClient: QueryClient,
  companyId: string,
  updater: JobPosition[] | ((prev: JobPosition[]) => JobPosition[]),
): void {
  queryClient.setQueryData<JobPosition[]>(queryKeys.positions(companyId), (prev = []) =>
    typeof updater === 'function' ? updater(prev) : updater,
  );
}

export function setPositionCache(
  queryClient: QueryClient,
  companyId: string,
  positionId: string,
  updater: JobPosition | null | ((prev: JobPosition | null | undefined) => JobPosition | null),
): void {
  queryClient.setQueryData<JobPosition | null>(
    queryKeys.position(companyId, positionId),
    (prev) => (typeof updater === 'function' ? updater(prev ?? null) : updater),
  );
}

export function patchApplicationsCache(
  queryClient: QueryClient,
  companyId: string,
  updates: Application[] | ((prev: Application[]) => Application[]),
  archived = false,
): void {
  const sync = createApplicationListSync(queryClient);
  if (typeof updates === 'function') {
    sync.patchCompanyUpdater(companyId, updates, archived);
    return;
  }
  sync.patchMany(companyId, updates, archived);
}

/** Optimistic list sync after unlock-deferred (PENDING_UPGRADE → READY_TO_PROCESS). */
export function patchUnlockedDeferredApplications(
  queryClient: QueryClient,
  companyId: string,
): void {
  createApplicationListSync(queryClient).patchCompanyUpdater(companyId, (prev) => {
    let changed = false;
    const next = prev.map((a) => {
      if (a.archived === true || a.state !== 'PENDING_UPGRADE') return a;
      changed = true;
      return { ...a, state: 'READY_TO_PROCESS' as const };
    });
    return changed ? next : prev;
  }, false);
}

export async function appendManualApplicationToCache(
  queryClient: QueryClient,
  companyId: string,
  positionId: string,
  applicationId: string,
): Promise<void> {
  await createApplicationListSync(queryClient).upsertOne(companyId, positionId, applicationId);
}

/** Immediate UI after bulk archive — before network refetch settles. */
export function applyBulkArchiveToCache(
  queryClient: QueryClient,
  companyId: string,
  apps: Application[],
): void {
  if (apps.length === 0) return;
  const ids = new Set(apps.map((a) => a.id));
  const now = new Date().toISOString();
  const archivedRows: Application[] = apps.map((a) => ({
    ...a,
    archived: true,
    archivedAt: a.archivedAt ?? now,
  }));

  const stripActive = (prev: Application[] | undefined): Application[] | undefined =>
    (prev ? removeApplicationsById(prev, ids) : prev);

  const sync = createApplicationListSync(queryClient);
  sync.stripFromActiveListCaches(companyId, ids);
  sync.patchMany(companyId, archivedRows, true);

  const touchedPositions = new Set(archivedRows.map((a) => resolveApplicationPositionId(a)));
  for (const positionId of touchedPositions) {
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, false),
      stripActive,
    );
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, true),
      (prev) => mergeApplicationsById(prev ?? [], archivedRows),
    );
  }

  sync.bumpActiveCount(companyId, -archivedRows.length);
  sync.bumpArchivedCount(companyId, archivedRows.length);
}

/** Immediate UI after bulk restore — before network refetch settles. */
export function applyBulkUnarchiveToCache(
  queryClient: QueryClient,
  companyId: string,
  apps: Application[],
): void {
  if (apps.length === 0) return;
  const ids = new Set(apps.map((a) => a.id));
  const now = new Date().toISOString();
  const activeRows: Application[] = apps.map((a) => {
    const { archived: _a, archivedAt: _at, archivedBy: _by, ...rest } = a;
    return {
      ...rest,
      archived: false,
      updatedAt: now,
    };
  });

  const sync = createApplicationListSync(queryClient);
  sync.stripFromArchivedListCaches(companyId, ids);
  sync.patchMany(companyId, activeRows, false, { skipTabCountInvalidation: true });

  const touchedPositions = new Set(activeRows.map((a) => resolveApplicationPositionId(a)));
  for (const positionId of touchedPositions) {
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, true),
      (prev) => (prev ? removeApplicationsById(prev, ids) : prev),
    );
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, false),
      (prev) => mergeApplicationsById(prev ?? [], activeRows),
    );
  }

  sync.bumpActiveCount(companyId, activeRows.length);
  sync.bumpArchivedCount(companyId, -activeRows.length);
}

/** Drop permanently deleted applications from all list caches (posting cascade, etc.). */
export function purgeApplicationIdsFromCache(
  queryClient: QueryClient,
  companyId: string,
  applicationIds: string[],
  options?: { activeDeletedCount?: number },
): void {
  if (applicationIds.length === 0) return;
  const ids = new Set(applicationIds);
  const sync = createApplicationListSync(queryClient);
  sync.stripFromActiveListCaches(companyId, ids);
  sync.stripFromArchivedListCaches(companyId, ids);
  removeRecentSettledApplications(companyId, ids);

  const strip = (prev: Application[] | undefined): Application[] | undefined =>
    (prev ? removeApplicationsById(prev, ids) : prev);
  const positionEntries = queryClient.getQueriesData<Application[]>({
    queryKey: ['applications', companyId],
  });
  for (const [key] of positionEntries) {
    if (key.length !== 4 || typeof key[2] !== 'string') continue;
    queryClient.setQueryData<Application[]>(key, (prev) => strip(prev) ?? []);
  }

  const activeRemoved = options?.activeDeletedCount ?? applicationIds.length;
  if (activeRemoved > 0) {
    sync.bumpActiveCount(companyId, -activeRemoved);
  }
}

/** Immediate UI after permanent delete from archive — before network refetch settles. */
export function applyBulkDeleteToCache(
  queryClient: QueryClient,
  companyId: string,
  apps: Application[],
): void {
  if (apps.length === 0) return;
  const ids = new Set(apps.map((a) => a.id));
  const sync = createApplicationListSync(queryClient);
  sync.stripFromArchivedListCaches(companyId, ids);
  sync.stripFromActiveListCaches(companyId, ids);
  removeRecentSettledApplications(companyId, ids);

  const strip = (prev: Application[] | undefined): Application[] | undefined =>
    (prev ? removeApplicationsById(prev, ids) : prev);
  const touchedPositions = new Set(apps.map((a) => resolveApplicationPositionId(a)));
  for (const positionId of touchedPositions) {
    if (!positionId) continue;
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, true),
      strip,
    );
    queryClient.setQueryData(
      queryKeys.positionApplications(companyId, positionId, false),
      strip,
    );
  }

  sync.bumpArchivedCount(companyId, -apps.length);
}

/** Sync active list + badge now; defer archived list refetch so optimistic rows are not wiped by a lagging BE page. */
export function scheduleApplicationArchiveInvalidation(
  queryClient: QueryClient,
  companyId: string,
  restoredToActive = false,
): void {
  createApplicationListSync(queryClient).scheduleArchiveInvalidation(companyId, restoredToActive);
}

/** Refetch active + archived application lists and the archive badge count. */
export async function invalidateApplicationArchiveQueries(
  queryClient: QueryClient,
  companyId: string,
): Promise<void> {
  scheduleApplicationArchiveInvalidation(queryClient, companyId);
}

export async function invalidateRecruiterListQueries(
  queryClient: QueryClient,
  companyId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.positions(companyId) }),
    queryClient.invalidateQueries({ queryKey: ['applications-list', companyId] }),
    queryClient.invalidateQueries({ queryKey: ['applications', companyId] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.postingsAll(companyId), exact: true }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyTeams(companyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.applicationsArchivedCount(companyId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.applicationsActiveCount(companyId) }),
  ]);
}
